import React from 'react';
import { BrowserRouter as Router, Route, Navigate, Routes, useLocation } from 'react-router-dom';
import Home from './component/Home';
import Cart from './component/Cart';
import ProductDetails from './component/ProductDetails';
import ProductList from './component/ProductList';
import AllProducts from './component/AllProducts';
import TermsAndConditions from './component/general_footer_pages/TermsAndConditions';
import RefundPolicy from './component/general_footer_pages/RefundPolicy';
import PrivacyPolicy from './component/general_footer_pages/PrivacyPolicy';
import CheckoutPage from './component/CheckoutPage';
import NotFound from './component/NotFound'; // A component for handling 404 component

import NavBar from './component/includes/NavBar';
import Login from './component/Login';
import Register from './component/Register';
import OrderSuccessful from './component/OrderSuccessful';
import Dashboard from './component/admin/Dashboard';
import SignIn from './component/admin/SignIn';
import Orders from './component/admin/Orders';
import AllProduct from './component/admin/AllProduct';
import FullDetails from './component/admin/FullDetails';
import AddProduct from './component/admin/AddProduct';
import BeforeEmailVerification from './component/BeforeEmailVerification';
import EmailVerified from './component/EmailVerified';
import SignUp from './component/admin/SignUp';
import ForgotPassword from './component/ForgotPassword';
import ChangeForgottenPassword from './component/ChangeForgottenPassword';
import { AuthProvider, useAuth } from './firebase/AuthContext';
import './App.css';
import Profile from './component/shopper/Profile';
import AddCategoryForm from './component/admin/AddCategoryForm';
import AddSubCategoryForm from './component/admin/AddSubCategoryForm';
import AddGenderForm from './component/admin/AddGenderForm';
import { AddAPhotoSharp } from '@mui/icons-material';
import AddCollectionForm from './component/admin/AddCollectionForm';
import AddSeasonForm from './component/admin/AddSeasonForm';
import AddColorForm from './component/admin/AddColorForm';
import AddSizeForm from './component/admin/AddSizeForm';
import EditProduct from './component/admin/EditProduct';
import { FavoriteProvider } from './component/contexts/FavoriteContext';
import { CartProvider } from './component/contexts/CartContext';
import Favorites from './component/Favorites';
import CategoryPage from './component/CategoryPage';
import About from './component/general_footer_pages/About';
import Contact from './component/general_footer_pages/Contact';
import OrderDetails from './component/admin/OrderDetails';
import ComingSoon from './component/ComingSoon';

const ProtectedRoute = ({ children, roles }) => {
  const { currentUser, userRole } = useAuth();
  const location = useLocation();

  if (!currentUser) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  if (roles && !roles.includes(userRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const AppContent = () => {
  const location = useLocation();

  const noNavBarRoutes = [
    '/404',
    '/order-successful',

    // Admin routes
    '/a/sign-up',
    '/a/sign-in',
    '/a/dashboard',
    '/a/orders',
    '/a/all-products',
    '/a/add-product',
    '/a/edit-product', // Adjusted to handle dynamic segments
    '/a/add-category',
    '/a/add-sub-category',
    '/a/add-gender',
    '/a/add-collection',
    '/a/add-season',
    '/a/add-color',
    '/a/add-size',
    '/a/order',
   

    // Other routes
    '/verify-email',
    '/email-verified',
    '/forgot-password',
    '/change-password'
  ];

  const shouldHideNavBar = () => {
    const path = location.pathname;
    // Directly match '/a/edit-product' and other specific routes
    return noNavBarRoutes.some(route => {
      // Match exact route or dynamic routes with parameters
      if (route === '/a/edit-product') {
        return path.startsWith('/a/edit-product');
      }

      if (route === '/a/order') {
        return path.startsWith('/a/order');
      }

    
      const pattern = new RegExp(`^${route.replace(/:\w+/, '\\d*')}$`);
      return pattern.test(path);
    });
  };

  const showNavBar = !shouldHideNavBar();

  return (
    <div className="App">
      {showNavBar && <NavBar />}
      <div style={{ marginTop: showNavBar ? "20vh" : 0 }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/coming-soon" element={<ComingSoon /> } />

          <Route path="/sign-in" element={<Login />} />
          <Route path="/sign-up" element={<Register />} />
          <Route path='/verify-email' element={<BeforeEmailVerification />} />
          <Route path='/email-verified' element={<EmailVerified />} />

          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/change-password" element={<ChangeForgottenPassword />} />

          <Route path="/cart" element={<Cart />} />
          <Route path="/favorites" element={<Favorites />} />

          <Route path="/p/:id" element={<ProductDetails />} />
          <Route path="/products/:category_name" element={<ProductList />} />
          <Route path="/all-products" element={<AllProducts /> } />
          
          
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/order-successful" element={<OrderSuccessful />} />

          {/** filtered products routes */}
          <Route path="/c/:category_name" element={<CategoryPage />} />

          {/**  PERSONAL SHOPPER PAGES */}
          <Route path="/profile" element={
            <ProtectedRoute roles={['shopper']}>
               <Profile />
            </ProtectedRoute>}
          />

          {/* ADMIN PAGES */}
          <Route path="/a/dashboard" element={
            <ProtectedRoute roles={['admin']}>
              <Dashboard />
            </ProtectedRoute>
          } />

          <Route path="/a/order/:id" element={
            <ProtectedRoute roles={['admin']}>
              <OrderDetails />
            </ProtectedRoute>
          } />

          <Route path="/a/all-products" element={
            <ProtectedRoute roles={['admin']}>
              <AllProduct />
            </ProtectedRoute>
          } />

          <Route path="/a/add-product" element={
            <ProtectedRoute roles={['admin']}>
              <AddProduct />
            </ProtectedRoute>
          } />

          <Route
            path="/a/edit-product/:id"
            element={
              <ProtectedRoute roles={['admin']}>
                <EditProduct />
              </ProtectedRoute>
            }
          />

          <Route path="/a/add-category" element={
           <ProtectedRoute roles={['admin']}>
              <AddCategoryForm />
            </ProtectedRoute>
          } /> 


          <Route path="/a/add-sub-category" element={
           <ProtectedRoute roles={['admin']}>
              <AddSubCategoryForm />
            </ProtectedRoute>
          } />

          <Route path="/a/add-gender" element={
           <ProtectedRoute roles={['admin']}>
              <AddGenderForm />
            </ProtectedRoute>
          } />

          <Route path="/a/add-collection" element={
           <ProtectedRoute roles={['admin']}>
              <AddCollectionForm />
            </ProtectedRoute>
          } />

          <Route path="/a/add-season" element={
           <ProtectedRoute roles={['admin']}>
              <AddSeasonForm />
            </ProtectedRoute>
          } />

          <Route path="/a/add-color" element={
           <ProtectedRoute roles={['admin']}>
              <AddColorForm />
            </ProtectedRoute>
          } />

          <Route path="/a/add-size" element={
           <ProtectedRoute roles={['admin']}>
              <AddSizeForm />
            </ProtectedRoute>
          } />

          <Route path="/a/full-details" element={
            <ProtectedRoute roles={['admin']}>
              <FullDetails />
            </ProtectedRoute>
          } />

          
          <Route path="/a/sign-in" element={<SignIn />} />


          <Route path="/a/sign-up" element={
            <ProtectedRoute roles={['admin']}>
              <SignUp />
            </ProtectedRoute>
          } />
          <Route path="/a/orders" element={
            <ProtectedRoute roles={['admin']}>
              <Orders />
            </ProtectedRoute>
          } />

          <Route path="/a/order/:id" element={
            <ProtectedRoute roles={['admin']}>
              <OrderDetails />
            </ProtectedRoute>
          } />

          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} /> {/* This will redirect any undefined routes to /404 */}
        </Routes>
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <CartProvider>
          <FavoriteProvider>
              <Router>
                  <AppContent />
              </Router>
          </FavoriteProvider>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
